import type { CollectionWagsListing } from "@/features/listings";
import { toJsonLdScriptContent } from "@/lib/ui/jsonLd";
import { dogListingJsonLdImages } from "./dog-share-media";
import type { DogPageMode } from "./dog-listing-presentation";

type DogListing = CollectionWagsListing;

const JSON_LD_DESCRIPTION_MAX_LENGTH = 5000;

function jsonLdDescription(description: string | null | undefined) {
    const trimmed = description?.trim();
    if (!trimmed) return undefined;
    if (trimmed.length <= JSON_LD_DESCRIPTION_MAX_LENGTH) return trimmed;
    const clipped = trimmed.slice(0, JSON_LD_DESCRIPTION_MAX_LENGTH);
    const lastSpace = clipped.lastIndexOf(" ");
    return (lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trimEnd();
}

export function buildListingJsonLd({
    mode,
    dog,
    dogId,
    dogName,
    breedNamesDisplay,
    mediaItems,
    breederName,
    priceRange,
}: {
    mode: DogPageMode;
    dog: DogListing;
    dogId: string;
    dogName: string;
    breedNamesDisplay: string;
    mediaItems: Array<{ url: string; mimeType?: string | null }>;
    breederName?: string;
    priceRange: DogListing["priceRange"];
}) {
    if (mode === "draft") return null;
    const url = `https://www.wags.com/dog/${dogId}`;
    const exactPrice = getExactPrice(priceRange);
    const completeRange = getCompletePriceRange(priceRange);

    const schemaAvailability =
        dog.status === "unavailable"
            ? "https://schema.org/SoldOut"
            : dog.status === "pending"
              ? "https://schema.org/LimitedAvailability"
              : "https://schema.org/InStock";
    const seller = breederName
        ? {
              "@type": "Organization",
              name: breederName,
              address: dog.organization
                  ? {
                        "@type": "PostalAddress",
                        addressLocality: dog.organization.city ?? undefined,
                        addressRegion: dog.organization.state ?? undefined,
                        addressCountry: "US",
                    }
                  : undefined,
          }
        : undefined;
    const offers =
        exactPrice !== null
            ? {
                  "@type": "Offer",
                  price: exactPrice,
                  priceCurrency: priceRange?.currency,
                  availability: schemaAvailability,
                  url,
              }
            : completeRange
              ? {
                    "@type": "AggregateOffer",
                    lowPrice: completeRange.min,
                    highPrice: completeRange.max,
                    priceCurrency: priceRange?.currency,
                    availability: schemaAvailability,
                    url,
                }
              : undefined;
    const photos = dogListingJsonLdImages(mediaItems);
    return toJsonLdScriptContent({
        "@context": "https://schema.org",
        "@type": "Product",
        name: dogName,
        description: jsonLdDescription(dog.description),
        image: photos.length > 0 ? photos : undefined,
        url,
        category: breedNamesDisplay || "Dog",
        brand: { "@type": "Brand", name: "Wags" },
        seller,
        offers,
    });
}

function getExactPrice(priceRange: DogListing["priceRange"]) {
    if (priceRange?.min == null || priceRange.max == null) return null;
    return priceRange.min === priceRange.max ? priceRange.min : null;
}

function getCompletePriceRange(priceRange: DogListing["priceRange"]) {
    if (priceRange?.min == null || priceRange.max == null) return null;
    return { min: priceRange.min, max: priceRange.max };
}
